<template>
    <!-- 全屏 canvas 粒子背景：固定铺底，鼠标会轻微吸引附近粒子，制造"星云交互"感 -->
    <canvas ref="cv" class="particle-field" aria-hidden="true"></canvas>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from "vue";

// density 控制粒子密度（按屏幕面积换算数量），低端机可调小
const props = defineProps({
    density: { type: Number, default: 0.00009 },
    maxParticles: { type: Number, default: 120 },
    linkDist: { type: Number, default: 130 },
});

const cv = ref(null);

let ctx = null;
let particles = [];
let raf = 0;
let w = 0;
let h = 0;
let dpr = 1;

// 鼠标位置，用于粒子吸引；初始放在屏幕外避免一进来就聚拢
const mouse = { x: -9999, y: -9999 };

// 主题色，粒子和连线在这几种霓虹色里取
const COLORS = ["124,92,255", "0,229,255", "0,255,168"];

function resize() {
    const c = cv.value;
    if (!c) return;
    dpr = Math.min(window.devicePixelRatio || 1, 2); // 限制 2 倍，避免高分屏过载
    w = window.innerWidth;
    h = window.innerHeight;
    c.width = w * dpr;
    c.height = h * dpr;
    c.style.width = w + "px";
    c.style.height = h + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    seed();
}

// 根据面积重新播种粒子数量
function seed() {
    const target = Math.min(props.maxParticles, Math.floor(w * h * props.density));
    particles = Array.from({ length: target }, () => spawn());
}

function spawn() {
    return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.8 + 0.6,
        color: COLORS[(Math.random() * COLORS.length) | 0],
    };
}

function step() {
    ctx.clearRect(0, 0, w, h);

    for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // 鼠标在一定范围内会轻轻吸引粒子，超出则自由漂移
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const dist2 = dx * dx + dy * dy;
        if (dist2 < 24000) {
            p.vx += (dx / 1600) * 0.02;
            p.vy += (dy / 1600) * 0.02;
        }

        p.x += p.vx;
        p.y += p.vy;

        // 速度阻尼，避免越吸越快
        p.vx *= 0.985;
        p.vy *= 0.985;

        // 边界回弹
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;

        // 画粒子
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},0.9)`;
        ctx.fill();

        // 邻近粒子连线，形成网状结构
        for (let j = i + 1; j < particles.length; j++) {
            const q = particles[j];
            const lx = p.x - q.x;
            const ly = p.y - q.y;
            const d = Math.hypot(lx, ly);
            if (d < props.linkDist) {
                const alpha = (1 - d / props.linkDist) * 0.25;
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(q.x, q.y);
                ctx.strokeStyle = `rgba(${p.color},${alpha})`;
                ctx.lineWidth = 1;
                ctx.stroke();
            }
        }
    }

    raf = requestAnimationFrame(step);
}

function onMove(e) {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
}

function onLeave() {
    mouse.x = -9999;
    mouse.y = -9999;
}

onMounted(() => {
    ctx = cv.value.getContext("2d");
    resize();
    step();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave);
});

onBeforeUnmount(() => {
    cancelAnimationFrame(raf);
    window.removeEventListener("resize", resize);
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseout", onLeave);
});
</script>

<style scoped>
.particle-field {
    position: fixed;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    opacity: .7;
}
</style>
